/**
 * Remark plugin that turns inline `<b>` / `<strong>` / `<i>` / `<em>`
 * tags in streamed assistant text into real mdast `strong` / `emphasis`
 * nodes at markdown render time.
 *
 * See `inline-emphasis-tags.ts` for the marker grammar and the
 * standalone (unit-testable) splitter — this plugin is a thin
 * `unist-util-visit` wrapper that splices the splitter's output into
 * the AST.
 *
 * Background (PLT-4806): the chat surface ships an HTML-like inline
 * directive set (`<badge>`, `<cite>`, `<private />`). Janne asked that
 * "for any formatting tags that have obvious pure-html matches,
 * probably best to use the same html-style naming everywhere — so
 * `<b>` for bold etc." react-markdown does NOT render raw HTML by
 * default and we have no `rehype-raw` dependency, so the only path
 * is a remark plugin that converts the tags to real mdast nodes
 * before rendering — same shape as `remarkCitationPills`,
 * `remarkToneBadges`, and `remarkLockBadges`.
 *
 * Markdown `**bold**` and `*italic*` keep working unchanged: they are
 * already turned into `strong` / `emphasis` upstream by `remarkParse`,
 * before this plugin runs.
 *
 * Two passes (PLT-5054):
 *
 *   1. `text` pass — handles the rare case where an emphasis tag survives
 *      inside a single `text` node value (`splitTextOnEmphasisMarkers`).
 *   2. `parent` pass — the common production case. `remarkParse`/micromark
 *      tokenises a paired `<b>bold</b>` into THREE sibling nodes
 *      (`html('<b>')`, `text('bold')`, `html('</b>')`) because `<b>` is a
 *      recognised raw-HTML inline tag. The `text`-only pass never sees
 *      those `html` fragments, so without this pass they leak raw to the
 *      DOM (we don't run `rehype-raw`). `reconstructEmphasisFromHtmlNodes`
 *      stitches the split siblings back into a `strong` / `emphasis` node.
 *      Mirrors the `html`-node fallthrough the citation plugin added in
 *      PLT-4896 / PLT-4945, adapted for paired tags (which span siblings).
 */
import { SKIP, visit } from 'unist-util-visit';
import type { Plugin } from 'unified';
import type { Root, Text } from 'mdast';
import {
  reconstructEmphasisFromHtmlNodes,
  splitTextOnEmphasisMarkers,
  type EmphasisReconNode,
  type EmphasisSplitChild,
} from './inline-emphasis-tags';

export const remarkInlineEmphasis: Plugin<[], Root> = () => {
  return tree => {
    visit(tree, 'text', (node: Text, index, parent) => {
      if (typeof index !== 'number' || !parent) return;
      const newChildren = splitTextOnEmphasisMarkers(node.value);
      if (newChildren === null) return;
      // mdast spec allows `strong` / `emphasis` wherever `text` is
      // allowed; the parent children type is a strict union that doesn't
      // include them for every variant (e.g. table cells), so cast to
      // keep TS happy. Same pattern as the citation / tone-badge / lock
      // plugins.
      (parent.children as unknown as EmphasisSplitChild[]).splice(index, 1, ...newChildren);
      return [SKIP, index + newChildren.length];
    });
    // Second pass: reconstruct emphasis spans micromark split across
    // sibling html/text nodes (the common paired-tag case). Visiting
    // every container node and rebuilding its children in place is safe —
    // `reconstructEmphasisFromHtmlNodes` recurses into the spans it mints,
    // so re-running it on already-processed children returns null (no-op).
    visit(tree, node => {
      const withChildren = node as unknown as { children?: unknown };
      if (!Array.isArray(withChildren.children)) return;
      const rebuilt = reconstructEmphasisFromHtmlNodes(withChildren.children as EmphasisReconNode[]);
      if (rebuilt === null) return;
      withChildren.children = rebuilt;
    });
  };
};
